// Bean gibberish (§13.5) and the announcer (§13.3) as offline recipes on the formant synth.
// Beans: 15 syllables ({b, t, k} × {a, e, i, o, u}), 120 ms each, at a base pitch of 220 Hz on a
// saw source with a noise-burst onset, plus the line words (hwah!, hup!, oh-oh-oh-oh, ya-hoo!,
// nyeh!, ow!, hot!) rendered whole so their pitch contours are sung, not stitched. Each bean
// repitches them with playbackRate (its 0.8–1.45 pitch factor). The announcer is a robotic carrier
// (saw 110 Hz plus a square an octave up at −12 dB) through fixed formant sequences.
import type { Recipe } from '../sfx/recipes';
import { phones, speak, type Token } from './speech';

export const BASE_PITCH = 220;
/** A smaller vocal tract: cartoon beans. */
const BEAN_FORMANTS = 1.16;

export const ONSETS = ['b', 't', 'k'] as const;
export const VOWEL_SET = ['a', 'e', 'i', 'o', 'u'] as const;
export type Syllable = `${(typeof ONSETS)[number]}${(typeof VOWEL_SET)[number]}`;
export const SYLLABLES: readonly Syllable[] = ONSETS.flatMap((o) => VOWEL_SET.map((v) => `${o}${v}` as Syllable));

function syllableRecipe(s: Syllable): Recipe {
  const onset = s[0] as 'b' | 't' | 'k';
  const vowel = s[1] as (typeof VOWEL_SET)[number];
  return {
    dur: 0.2,
    render(k) {
      // The stop closure + burst take ~45 ms; the vowel fills the rest of the 120 ms syllable.
      const seq: Token[] = [onset, [vowel, 0.075]];
      const p = BASE_PITCH * k.jitter(0.01);
      speak(k, phones(seq), { pitch: [[0, p * 1.04], [0.05, p], [0.12, p * 0.94]], carrier: 'bean', formantScale: BEAN_FORMANTS, gain: 0.9 });
    },
  };
}

function word(seq: Token[], pitch: readonly (readonly [number, number])[], dur: number, vibrato?: [number, number], gain = 0.9): Recipe {
  return {
    dur,
    render(k) {
      speak(k, phones(seq), { pitch, carrier: 'bean', formantScale: BEAN_FORMANTS, vibrato, gain });
    },
  };
}

export const BEAN_SYLLABLES: Readonly<Record<Syllable, Recipe>> = Object.fromEntries(SYLLABLES.map((s) => [s, syllableRecipe(s)])) as Record<Syllable, Recipe>;

/** §13.5 voice lines. */
export const BEAN_LINES = {
  // "hwah!": a "ba" with a falling bend (aspirated, as if winded).
  hit: word(['h', 'w', ['a', 0.13]], [[0, 330], [0.06, 300], [0.2, 190]], 0.32),
  throw: word(['h', ['uh', 0.09], 'p'], [[0, 250], [0.08, 285]], 0.25),
  teeter: word(['gap', ['o', 0.12], 'gap', ['o', 0.12], 'gap', ['o', 0.12], 'gap', ['o', 0.16]],
    [[0, 205], [0.15, 205], [0.16, 228], [0.3, 228], [0.31, 250], [0.45, 250], [0.46, 285], [0.68, 290]], 0.8, [7, 30]),
  ko: word(['y', ['a', 0.11], 'h', ['u', 0.24]], [[0, 255], [0.12, 270], [0.17, 330], [0.36, 360], [0.45, 330]], 0.58, [6, 20]),
  grabbed: word(['n', 'y', ['e', 0.15]], [[0, 300], [0.05, 290], [0.24, 240]], 0.32),
  stomp: word([['aw', 0.08], ['u', 0.09]], [[0, 360], [0.17, 230]], 0.26),
  hot: word(['h', ['aw', 0.11], 't'], [[0, 300], [0.12, 330]], 0.28),
  grunt: word(['h', ['uh', 0.06], 'n'], [[0, 200], [0.12, 180]], 0.2, undefined, 0.6),
} satisfies Record<string, Recipe>;
export type BeanLine = keyof typeof BEAN_LINES;

// ---- Announcer -------------------------------------------------------------------------------

function announce(seq: Token[], pitch: readonly (readonly [number, number])[], dur: number): Recipe {
  return {
    dur,
    render(k) {
      speak(k, phones(seq), { pitch, carrier: 'robot', formantScale: 0.96, gain: 1 });
    },
  };
}

export const ANNOUNCER = {
  three: announce(['th', 'r', ['i', 0.26]], [[0, 132], [0.12, 128], [0.4, 104]], 0.55),
  two: announce(['t', ['u', 0.28]], [[0, 132], [0.1, 128], [0.36, 104]], 0.5),
  one: announce(['w', ['uh', 0.2], 'n'], [[0, 134], [0.1, 130], [0.36, 106]], 0.5),
  bonk: announce(['b', ['aw', 0.24], 'ng', 'k'], [[0, 170], [0.08, 178], [0.34, 112]], 0.55),
  suddenDeath: announce(['s', ['uh', 0.09], 'd', ['ih', 0.07], 'n', 'gap', 'd', ['e', 0.2], 'th'],
    [[0, 150], [0.2, 140], [0.36, 165], [0.62, 118]], 0.85),
  matchPoint: announce(['m', ['ae', 0.13], 'ch', 'gap', 'p', ['o', 0.1], ['i', 0.08], 'n', 't'],
    [[0, 140], [0.18, 135], [0.32, 162], [0.6, 118]], 0.8),
  game: announce(['g', ['e', 0.26], ['i', 0.08], 'm'], [[0, 165], [0.1, 175], [0.42, 110]], 0.6),
  doubleBonk: announce(['d', ['uh', 0.09], 'b', 'l', 'gap', 'b', ['aw', 0.2], 'ng', 'k'],
    [[0, 150], [0.18, 140], [0.28, 170], [0.62, 112]], 0.8),
  draw: announce(['d', 'r', ['aw', 0.34]], [[0, 150], [0.12, 150], [0.5, 105]], 0.6),
} satisfies Record<string, Recipe>;
export type AnnouncerWord = keyof typeof ANNOUNCER;
